import { useState } from "react";

import { useTranslation } from "@refinedev/core";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Box, FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, Typography } from "@mui/material";

// Password requirement validation functions
export const passwordRequirements = [
  { key: "length", check: (pw: string) => pw.length >= 8 },
  { key: "number", check: (pw: string) => /\d/.test(pw) },
  { key: "special", check: (pw: string) => /[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/.test(pw) },
  { key: "uppercase", check: (pw: string) => /[A-Z]/.test(pw) },
  { key: "lowercase", check: (pw: string) => /[a-z]/.test(pw) },
];

// Hook for password validation logic
export const usePasswordValidation = (newPassword: string, confirmPassword: string) => {
  const passwordsMatch = newPassword === confirmPassword;
  const allRequirementsMet = passwordRequirements.every(req => req.check(newPassword));
  return { passwordsMatch, allRequirementsMet };
};

interface PasswordEntryFieldsProps {
  newPassword: string;
  confirmPassword: string;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
}

export const PasswordEntryFields = ({
  newPassword,
  confirmPassword,
  onNewPasswordChange,
  onConfirmPasswordChange,
}: PasswordEntryFieldsProps) => {
  const { translate } = useTranslation();

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { passwordsMatch } = usePasswordValidation(newPassword, confirmPassword);

  return (
    <>
      {/* New Password Field */}
      <FormControl fullWidth variant="outlined">
        <InputLabel shrink required>
          {translate("pages.setupNewPassword.newPassword", "New Password")}
        </InputLabel>
        <OutlinedInput
          type={showNewPassword ? "text" : "password"}
          value={newPassword}
          onChange={e => onNewPasswordChange(e.target.value)}
          inputProps={{ "data-testid": "new-password", autoComplete: "new-password" }}
          placeholder="••••••••••"
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label={showNewPassword ? "hide the password" : "display the password"}
                onClick={() => setShowNewPassword(!showNewPassword)}
                onMouseDown={e => e.preventDefault()}
                onMouseUp={e => e.preventDefault()}
                edge="end"
                sx={{ color: "text.secondary" }}
              >
                {showNewPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>

      {/* Confirm Password Field */}
      <FormControl fullWidth variant="outlined">
        <InputLabel shrink required>
          {translate("pages.setupNewPassword.confirmPassword", "Confirm New Password")}
        </InputLabel>
        <OutlinedInput
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={e => onConfirmPasswordChange(e.target.value)}
          inputProps={{ "data-testid": "confirm-password", autoComplete: "new-password" }}
          placeholder="••••••••••"
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label={showConfirmPassword ? "hide the password" : "display the password"}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                onMouseDown={e => e.preventDefault()}
                onMouseUp={e => e.preventDefault()}
                edge="end"
                sx={{ color: "text.secondary" }}
              >
                {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>

      {/* Password Requirements List */}
      <Box component="ul" pl={2} m={0}>
        {passwordRequirements.map(req => {
          const isMet = req.check(newPassword);
          return (
            <Typography
              component="li"
              variant="caption"
              key={req.key}
              sx={{
                color: isMet ? "success.main" : "text.disabled",
                py: 0.25,
              }}
            >
              {translate(
                `pages.setupNewPassword.requirements.${req.key}`,
                req.key === "length"
                  ? "Must be at least 8 characters"
                  : req.key === "number"
                    ? "Must contain at least one number"
                    : req.key === "special"
                      ? "Must contain at least one special character (^$*.[]{}()?-\"!@#%&/\\,><':;|_~`+=)"
                      : req.key === "uppercase"
                        ? "Must contain at least one uppercase character"
                        : "Must contain at least one lowercase character"
              )}
            </Typography>
          );
        })}
      </Box>

      {/* Password Mismatch Warning */}
      {confirmPassword.length > 0 && !passwordsMatch && (
        <Typography color="error.dark" variant="body2" sx={{ mt: -1 }}>
          {translate("pages.setupNewPassword.passwordsMustMatch", "Passwords must match")}
        </Typography>
      )}
    </>
  );
};
